/**
 * GL management tree API
 * GET /api/gl-maps/management-tree
 */

import { getStoredToken } from "../../../../services/api";
import { API_BASE_URL } from "../../../../config/api";
import type { GlManagementTreeResponseDTO } from "../models/GlDTOs";

class APIError extends Error {
  public status: number;
  public data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
};

async function getToken(): Promise<string> {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }
  return token;
}

/**
 * Fetch hierarchical GL tree for management views (Masters + Admin).
 */
export async function fetchGlManagementTree(params: {
  limit: number;
  offset: number;
  search?: string;
}): Promise<GlManagementTreeResponseDTO> {
  const token = await getToken();
  const query = new URLSearchParams();
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.search) {
    query.set("search", params.search);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/gl-maps/management-tree?${query.toString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      (data as { message?: string }).message ||
        `Failed to fetch GL management tree: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as GlManagementTreeResponseDTO;
}
